import {
  getDeploy,
  getDeployCount,
  getDeployEnvVars,
  getDeployRuntimeLogs,
  getDeploys,
  getRecentDeploys,
} from "@/lib/deploy/api";
import { DEPLOY_STATUS } from "@/lib/enums";
import {
  ApplicationLogEntry,
  Deploy,
  EnvironmentVariables,
  GetManyResponse,
} from "@/types";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";

const createEmptyResponse = <T>(): GetManyResponse<T> => ({
  limit: 0,
  offset: 0,
  hasNext: false,
  total: 0,
  items: [],
});

export function useGetDeploy(deployId: string) {
  return useSuspenseQuery({
    queryKey: ["deploy", deployId],
    queryFn: async () => {
      try {
        return await getDeploy(deployId);
      } catch (error) {
        console.error("Failed to fetch deploy:", error);
        return { data: {} };
      }
    },
    select: (data) => {
      try {
        return (data?.data || {}) as Deploy;
      } catch (error) {
        console.error("Error in deploy select:", error);
        return {} as Deploy;
      }
    },
    refetchInterval: (query) => {
      const deploy = query.state.data as Deploy | undefined;
      // Keep polling unless deploy is complete, failed, or archived
      if (
        !deploy ||
        [DEPLOY_STATUS.FAILED, DEPLOY_STATUS.COMPLETE].includes(deploy.status)
      ) {
        return false; // Stop polling when deploy is complete, failed, or archived
      }
      return 5000; // Poll every 5 seconds for in-progress, new, or deleting deploys
    },
    refetchIntervalInBackground: true,
  });
}

export function useGetDeployEnvVars(deployId: string, enabled = false) {
  return useQuery({
    queryKey: ["deployEnvVars", deployId],
    queryFn: () => getDeployEnvVars(deployId),
    select: (data) => data.data as EnvironmentVariables[],
    enabled,
  });
}

export function useGetDeployRuntimeLogs(
  deployId: string,
  params: {
    startTime: number;
    endTime: number;
    limit?: number;
    offset?: number;
    level?: string;
    search?: string;
  },
  enabled = true,
) {
  const { startTime, endTime, limit = 200, offset = 0, level, search } = params;

  return useQuery({
    queryKey: [
      "deployLogs",
      deployId,
      startTime,
      endTime,
      limit,
      offset,
      level ?? null,
      search ?? null,
    ],
    queryFn: () =>
      getDeployRuntimeLogs(deployId, {
        startTime,
        endTime,
        limit,
        offset,
        level,
        search,
      }),
    select: (data) => data.data as GetManyResponse<ApplicationLogEntry>,
    enabled,
  });
}

export function useGetTotalDeploys() {
  return useSuspenseQuery({
    queryKey: ["deploy", "active"],
    queryFn: async () => {
      try {
        return await getDeployCount();
      } catch (error) {
        console.error("Failed to fetch deploy count:", error);
        return { data: 0 };
      }
    },
    select: (data) => {
      try {
        return data?.data ?? 0;
      } catch (error) {
        console.error("Error in deploy count select:", error);
        return 0;
      }
    },
    staleTime: 10000,
  });
}

export function useGetRecentDeploys() {
  return useSuspenseQuery({
    queryKey: ["deploy", "recent"],
    queryFn: async () => {
      try {
        return await getRecentDeploys();
      } catch (error) {
        console.error("Failed to fetch recent deploys:", error);
        return { data: { items: [] } };
      }
    },
    select: (data) => {
      try {
        if (data?.data?.items) {
          return data.data.items as Deploy[];
        }
        if (Array.isArray(data?.data)) {
          return data.data as Deploy[];
        }
        return [] as Deploy[];
      } catch (error) {
        console.error("Error in recent deploys select:", error);
        return [] as Deploy[];
      }
    },
    staleTime: 10000,
  });
}

export function useGetDeploys(
  limit: number,
  offset: number,
  searchTerm: string,
) {
  return useSuspenseQuery({
    queryKey: ["deploy", limit, offset, searchTerm],
    queryFn: async () => {
      try {
        return await getDeploys(limit, offset, searchTerm);
      } catch (error) {
        console.error("Failed to fetch deploys:", error);
        return { data: createEmptyResponse<Deploy>() };
      }
    },
    select: (data) => {
      try {
        const payload = data?.data as GetManyResponse<Deploy> | undefined;
        return payload ?? createEmptyResponse<Deploy>();
      } catch (error) {
        console.error("Error in deploys select:", error);
        return createEmptyResponse<Deploy>();
      }
    },
  });
}
